// Statuses that mark a case as Non-current — shared across asset, liability, and policy.
// Any status NOT in this list (including blank/null) is treated as Current.
// Comparison is case- and separator-insensitive via normalizeStatus.
export const NON_CURRENT_STATUSES: string[] = [
  "Cancelled",
  "Declined",
  "Matured",
  "Lapsed",
  "Terminated",
  "Surrendered",
  "Transferred",
  "Redeemed",
  "Re-paid",
  "Not Proceeded With",
  "Expired",
  "Converted",
  "Not Taken Up",
  "Out Of Force",
];

// Normalises a case_type string for comparison: lowercased, hyphens and
// underscores replaced with spaces, and trimmed.  Keeps the filter resilient
// to DB values like "Unit-linked", "unit_linked", or "Unit Linked" all
// matching the same route segment.
export const normalizeCaseType = (s: string): string =>
  (s ?? "").toLowerCase().replace(/[-_]/g, " ").trim();

// Normalises a status string for comparison: same rules as normalizeCaseType.
// Lets "CANCELLED", "cancelled", "Cancelled", "NOT_TAKEN_UP", "not-taken-up"
// all match the canonical values in NON_CURRENT_STATUSES.
export const normalizeStatus = (s: string): string =>
  (s ?? "").toLowerCase().replace(/[-_]/g, " ").trim();

// Returns true when the status is NOT a known non-current status.
// Empty/null statuses are treated as current.
// The `module` param is kept for API compatibility — the denylist is now
// shared across asset, liability, and policy.
export const isCurrentStatus = (
  _module: "asset" | "liability" | "policy",
  status: string
): boolean => {
  const n = normalizeStatus(status);
  return !NON_CURRENT_STATUSES.some((s) => normalizeStatus(s) === n);
};

// Extracts a status string from a case item regardless of which field the
// sub-type uses.  Investment assets expose `investment_status`; all other
// sub-types expose a generic `status` field.
export const getItemStatus = (item: any): string =>
  item?.investment_status ?? item?.status ?? "";

// Legacy — kept so any remaining callers don't break during migration.
// No longer used by the main fetch flow (status filtering is now client-side
// via isCurrentStatus / NON_CURRENT_STATUSES).
export const getCurrentStatusParam = (): string => "";
